import styled, { css } from 'styled-components';

// Teams-style equal-tile stage for the practice screen. Same visual vocabulary
// as the old layout: #111827/#1e2837 darks, 12px radii, the gold #ffd700
// question glow (promoted from the retired side-panel participant cards to a
// whole tile), amber #ffc107 "asking" accent, green #00ff88 speech accent.

export const TileGrid = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$centerLast',
})`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-content: center;
  flex: 1;
  min-height: 0;

  /* Rows are deliberately NOT forced to equal 1fr tracks. They were, and with
     ConferenceContainer on height:auto a 1fr track has no height to divide, so
     two rows split the little the tiles claimed on their own and every tile
     came out roughly 3.6:1. Row height now comes from each Tile's
     aspect-ratio, and the container grows to fit (still capped by its
     max-height, where the tiles compress rather than overflow). */

  /* An odd tile count leaves a hole in the second column. Centre the last tile
     across the two columns at one column's width so three people read as a
     balanced group rather than an L-shape with a gap. */
  ${(props) => props.$centerLast && css`
    & > *:last-child {
      grid-column: 1 / -1;
      justify-self: center;
      width: calc(50% - 8px);
    }
  `}
`;

export const Tile = styled.div.withConfig({
  shouldForwardProp: (prop) => !['$glow', '$clickable'].includes(prop),
})`
  /* Gives every tile a real height. Without it the tiles had none, so the
     auto-height stage collapsed and the grid had nothing to share out. */
  aspect-ratio: 16 / 9;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  min-height: 0;
  background: linear-gradient(145deg, #1e2837 0%, #111827 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 16px rgb(0 0 0 / 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  ${(props) => props.$clickable && css`cursor: pointer;`}

  /* The question audio is playing: the whole tile lights up. */
  ${(props) => props.$glow === 'bright' && css`
    background: linear-gradient(145deg, #3d3a2a 0%, #1c1a12 100%);
    border: 2px solid #ffd700;
    box-shadow: 0 0 15px #ffd700, 0 0 30px rgba(255, 215, 0, 0.27);
  `}

  /* Question pending, waiting on the user: a softer persistent highlight. */
  ${(props) => props.$glow === 'soft' && css`
    background: linear-gradient(145deg, #262418 0%, #14130d 100%);
    border: 1px solid rgba(255, 215, 0, 0.55);
    box-shadow: 0 0 12px rgba(255, 215, 0, 0.18);
  `}

  /* Active-speaker ring for the user's tile. Toggled as a CLASS (not a prop)
     by the mic-level subtree so ~8Hz level sampling never re-renders the
     recording screen — see UserTile. */
  &.speaking {
    border: 2px solid #00ff88;
    box-shadow: 0 0 12px rgba(0, 255, 136, 0.35);
  }

  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    background: black;
  }

  /* Pre-call placeholder inside the user tile (no camera yet / screen-share
     not started). Same markup + classes the old MainPresenterView carried. */
  .video-placeholder {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #2d2e30;
    /* Clear of the mic dock in the bottom-right corner. Without this the
       "click the start button" line ran underneath it. */
    padding: 16px 16px 68px;
    color: #fff;
    font-size: 1.2rem;

    img {
      width: 120px;
      opacity: 0.5;
      margin-bottom: 16px;
    }

    .placeholder-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    .status-overlay {
      position: absolute;
      bottom: 24px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 10;
    }
  }
`;

export const AvatarCircle = styled.div.withConfig({
  shouldForwardProp: (prop) => !['$bg', '$size'].includes(prop),
})`
  width: ${(props) => props.$size || 88}px;
  height: ${(props) => props.$size || 88}px;
  border-radius: 50%;
  background: ${(props) => props.$bg || '#202126'};
  border: 1px solid rgba(255, 255, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.9rem;
  font-weight: 700;
  flex-shrink: 0;

  img {
    width: 56%;
    height: 56%;
    object-fit: contain;
  }
`;

export const NameChip = styled.div`
  position: absolute;
  left: 12px;
  bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.55);
  padding: 5px 10px;
  border-radius: 8px;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  z-index: 2;
  max-width: 60%;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const CaptionBox = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$dimmed',
})`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 46px;
  max-width: 84%;
  background: rgba(0, 0, 0, ${(props) => (props.$dimmed ? 0.6 : 0.72)});
  border: 1px solid rgba(255, 215, 0, ${(props) => (props.$dimmed ? 0.25 : 0.45)});
  padding: 10px 14px;
  border-radius: 10px;
  color: ${(props) => (props.$dimmed ? 'rgba(245, 240, 220, 0.75)' : '#f5f0dc')};
  font-size: 0.9rem;
  line-height: 1.45;
  z-index: 2;
`;

export const AskingPill = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 7px;
  background: rgba(0, 0, 0, 0.55);
  border: 1px solid rgba(255, 193, 7, 0.6);
  padding: 5px 11px;
  border-radius: 999px;
  color: #ffc107;
  font-size: 0.78rem;
  font-weight: 700;
  z-index: 2;
`;

export const ListeningHint = styled.span`
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.8rem;
  font-weight: 600;
`;

/* Mic meter dock inside the user tile (Teams-style, bottom-right). */
export const MeterDock = styled.div`
  position: absolute;
  right: 12px;
  bottom: 12px;
  /* Wide enough for "No sound from your mic" on one line beside the glyph.
     At 130px it did not fit and spilled out of the dock. */
  width: 168px;
  background: rgba(0, 0, 0, 0.55);
  padding: 6px 8px;
  border-radius: 8px;
  z-index: 2;
`;

/* Countdown overlay for the L3 pre-roll (3-2-1). */
export const CountdownOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  z-index: 5;

  span {
    color: #ffc107;
    font-size: 5rem;
    font-weight: 700;
    animation: pulse 1s infinite;
  }
`;

export const BottomBar = styled.div`
  /* The button cluster is absolutely centred rather than laid out between the
     two flanking columns. Centring it with flex made it sit off-centre: the
     flanks were 140px and 200px, so the middle column's centre was 30px left
     of the bar's, and it would shift again whenever the right-hand Q&A
     indicator appeared or changed width. Absolute centring is independent of
     both flanks, which is what a call control bar actually wants. */
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  height: 60px;
  flex-shrink: 0;
  background: #202126;
  border-radius: 12px;
  padding: 0 16px;

  /* Equal flanks so neither column can crowd the other. They are the only
     items in flow now that .bar-center is absolute. */
  .bar-left,
  .bar-right {
    display: flex;
    align-items: center;
    flex: 1 1 0;
    min-width: 0;
  }

  .bar-left {
    justify-content: flex-start;
  }

  .bar-center {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    gap: 10px;

    /* The legacy .video-cta rule in App.css carries margin-bottom: 10px, and it
       still applies here because RecordingButton wraps every button in
       .button-card > .video-cta. That margin collapses through, so each
       button's margin box is 10px taller than the button, and centring that box
       sits the row ~5px high in the bar. Neutralised here rather than in
       App.css, which the v1 RecordPresentation page still relies on. */
    .button-card,
    .video-cta {
      margin: 0;
    }
  }

  .bar-right {
    justify-content: flex-end;
  }
`;
